import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const kit = {
  name: 'VeraBIND Tau',
  image: '/kit_0001.png',
  imageAlt: 'VeraBIND Tau kit',
  description:
    'First-in-class, blood-based assay quantifying hyperphosphorylated tau for early Alzheimer\'s detection; verified against tau-PET with over 96% agreement.',
};

const person = {
  firstName: 'Jane',
  lastName: 'Smith',
  dob: 'Jan 12, 1968',
  phone: '(555) 123-4567',
  emailOk: 'Yes',
};

const shipping = {
  line1: '123 Oak Street',
  line2: 'Apt 4B',
  city: 'San Diego',
  state: 'CA',
  zip: '92101',
};

const provider = {
  name: 'Dr. Lauren Martinez, MD',
  addressLine1: '4400 W. Research Parkway',
  addressLine2: 'Suite 210, San Diego, CA 92121',
};

const nextSteps = [
  {
    title: 'Receive your kit',
    description: 'We will ship your VeraBIND Tau kit to the address you provided.',
  },
  {
    title: 'Use the kit as directed',
    description: 'Follow the instructions included in the package to complete your sample.',
  },
  {
    title: 'Mail it back',
    description: 'Use the pre-labeled packaging to return your sample to our lab.',
  },
  {
    title: 'Provider review',
    description: 'Your assigned provider will review the results and contact you with next steps.',
  },
];

export default function CustomerPurchaseComplete() {
  return (
    <Container disableGutters sx={{ mt: 0 }}>
      <Box
        sx={{
          maxWidth: 'md',
          mx: 'auto',
          textAlign: 'left',
          pt: { xs: 0.5, md: 0.5 },
          pb: { xs: 0.5, md: 0.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Card
          sx={{
            border: '1px solid #97999B',
            borderRadius: 2,
            boxShadow: '0 18px 40px rgba(0, 0, 0, 0.08)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Typography variant="h4" component="h1">
              Purchase Complete
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Thank you. Your order is confirmed and the next steps are outlined below.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.4fr 0.9fr' },
                gap: 2,
                mt: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Person
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {person.firstName} {person.lastName}
                    </Typography>
                    <Typography variant="body2">DOB: {person.dob}</Typography>
                    <Typography variant="body2">Cell: {person.phone}</Typography>
                    <Typography variant="body2">Email updates: {person.emailOk}</Typography>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Shipping Address
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {shipping.line1}
                    </Typography>
                    <Typography variant="body2">{shipping.line2}</Typography>
                    <Typography variant="body2">
                      {shipping.city}, {shipping.state} {shipping.zip}
                    </Typography>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Assigned Provider
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {provider.name}
                    </Typography>
                    <Typography variant="body2">{provider.addressLine1}</Typography>
                    <Typography variant="body2">{provider.addressLine2}</Typography>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    border: '1px solid #97999B',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Next Steps
                    </Typography>
                    <List sx={{ p: 0, mt: 1 }}>
                      {nextSteps.map((step) => (
                        <ListItem key={step.title} sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={step.title}
                            secondary={step.description}
                            primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
                            secondaryTypographyProps={{ variant: 'body2', sx: { mt: 0.1 } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Card
                sx={{
                  border: '1px solid #97999B',
                  borderRadius: 2,
                  boxShadow: 'none',
                  alignSelf: 'start',
                }}
              >
                <CardMedia
                  component="img"
                  image={kit.image}
                  alt={kit.imageAlt}
                  sx={{
                    width: '100%',
                    height: 160,
                    objectFit: 'cover',
                    borderBottom: '1px solid #97999B',
                  }}
                />
                <CardContent>
                  <Typography variant="subtitle2" component="p">
                    Selected kit
                  </Typography>
                  <Typography variant="h6" component="h2" sx={{ mt: 0.5 }}>
                    {kit.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {kit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to="/mock/customer-landing"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  boxShadow: '0 12px 22px rgba(0, 118, 127, 0.28)',
                  py: 1.1,
                  px: 4,
                }}
              >
                Done
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
